/**
 * LazyPanelContent — mounts children on first accordion expand, stays mounted.
 *
 * Renders children only after the surrounding accordion has been expanded
 * at least once. Reads the live disclosure state from
 * `DisclosureStateContext` (the same context `AccordionPanel` itself
 * consumes) so this works for BOTH controlled and uncontrolled accordions
 * — keying off the controlled `isExpanded` prop alone leaves uncontrolled
 * sections permanently empty.
 *
 * Once expanded, the content stays mounted so subsequent collapse/expand
 * cycles animate smoothly and any nested state (scroll position, expanded
 * subrows) is preserved.
 */

import type { ReactNode } from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import { DisclosureStateContext } from 'react-aria-components';

export function LazyPanelContent({ children }: { children: ReactNode }) {
  const state = useContext(DisclosureStateContext);
  const isExpanded = state?.isExpanded ?? false;
  const hasBeenExpandedRef = useRef(false);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (isExpanded && !hasBeenExpandedRef.current) {
      // Defer the ref flip until the rAF actually fires so a rapid
      // open→close toggle (cleanup cancels the rAF) doesn't leave the
      // component in a "expanded once" state with `ready` still false,
      // which would permanently block content from mounting.
      const id = requestAnimationFrame(() => {
        hasBeenExpandedRef.current = true;
        setReady(true);
      });
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [isExpanded]);
  if (!ready) {
    return null;
  }
  return children;
}
